/**
 * The message TAP sends a client when a revised proof is ready.
 *
 * The Hub does not send email — that is open question 2 in the pipeline scope
 * and a whole batch of its own. What it can do is remove the part people
 * actually get wrong: finding the right review URL, for the right order, and
 * not pasting a token that belongs to a different client.
 *
 * So this builds the sentence and the clipboard does the rest. Deliberately
 * plain text: it is going into an email client, Slack, or WhatsApp, and any
 * markup would arrive as literal characters in at least one of them.
 */

export interface RevisionNoticeInput {
  clientName: string;
  componentName: string;
  orderNo: string;
  reviewUrl: string;
}

// The URL builder is NOT redefined here. clientLink.ts already owns it, and a
// second copy is how a link format drifts: the wizard would keep minting one
// shape while the revision notice sent another, and only the client would ever
// see both.
export { reviewUrl } from './clientLink';

/**
 * Whether there is enough to write a message worth sending.
 *
 * The URL is the only truly load-bearing part — a notice without it asks the
 * client to go and find the link, which is the thing this exists to avoid. The
 * name and component degrade to something readable instead of blocking.
 */
export function canWriteNotice(input: Partial<RevisionNoticeInput>): boolean {
  return typeof input.reviewUrl === 'string' && input.reviewUrl.trim() !== '';
}

/**
 * The notice, ready to paste.
 *
 * Missing pieces fall back rather than printing an empty bracket. "Hi ," is
 * the kind of detail that tells a client they were sent a template, and the
 * whole point of a prepared message is that it does not read like one.
 */
export function revisionNotice(input: RevisionNoticeInput): string {
  const client = input.clientName.trim();
  const component = input.componentName.trim();
  const order = input.orderNo.trim();
  const greeting = client === '' ? 'Hi there' : `Hi ${client}`;
  const what = component === '' ? 'a revised proof' : `a revised proof for ${component}`;
  // The order number is the client's own reference on every other document
  // they have had from TAP, so it is worth keeping even when the rest is thin.
  const which = order === '' ? '' : ` on order ${order}`;
  return `${greeting}, we've uploaded ${what}${which}. Please review and approve here: ${input.reviewUrl.trim()}`;
}
